import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import {Col, Row} from "reactstrap";
import Paper from "@mui/material/Paper";
import Button from "../../../../../../../components/base/button/button";
import {OutlinedDropdown} from "../../../../../../../components/base/dropdown/outlined_dropdown";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../../components/base/chips/chips";
import TagChipsContent from "../../../../../../../components/app-specific/chips-contents/tag-chips-content";
import ToastService from "../../../../../../../../core/services/toast";
import UploadCsvFileDialog from "../../../../../../../components/dialogs/upload-csv-file";
import useIndustry from "../../../../../../../hooks/use-industry";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import TagAutocomplete from "../../../../../../../components/text-fields/autocomplete/tag-autocomplete";
import PartAutocomplete from "../../../../../../../components/text-fields/autocomplete/part-autocomplete";


const initialUploadCsvFileDialogState = {
    open: false,
    requiredFields: [],
    title: '',
    sampleFileTitle: '',
    onParse: () => void 0,
}

const PrintCatalogueViewProductSelectionSection = ({
                                                       productSelectionState,
                                                       setProductSelectionState,
                                                       resetNextStates,
                                                       updatingStep,
                                                   }) => {
    const {cachedIndustry, industries, categoriesOfFetchedIndustry, hasNoIndustries, fetchedIndustry, fetchIndustry} = useIndustry();
    const [importingProductsFromCsv, setImportingProductsFromCsv] = useState(false);
    const [uploadCsvFileDialogState, setUploadCsvFileDialogState] = useState(initialUploadCsvFileDialogState);
    const isMounted = useIsMounted();

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        if (!cachedIndustry?.id)
            return;
        void fetchIndustry({industryId: cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry]);

    const allCategories = useMemo(() => {
        return categoriesOfFetchedIndustry.map((category) => ({
            ...category,
            colorCode: category.backgroundColor,
        }))
    }, [categoriesOfFetchedIndustry])

    /**
     * Fetches the cascading title of a category.
     * @type {(function(*): (*|string))}
     */
    const getCascadingTitle = useCallback((category) => {
        if (!category) return '';
        if (!category.parentId)
            return category.title;
        const parentCategory = allCategories.find(e => e.id === category.parentId);
        return `${getCascadingTitle(parentCategory)} -> ${category.title}`;
    }, [allCategories])

    const availableCategoryOptions = useMemo(() => {
        const selectedIds = productSelectionState.selectedCategories.map(e => e.id);
        return allCategories.filter(e => !selectedIds.includes(e.id)).map(e => ({
            value: e,
            label: getCascadingTitle(e),
            id: e.id
        }))
    }, [getCascadingTitle, allCategories, productSelectionState.selectedCategories])

    const existingPartIds = useMemo(() => productSelectionState.selectedProducts.map(e => e.id), [productSelectionState.selectedProducts])

    /**
     * Handles the industry change event.
     * @type {(function(*): void)}
     */
    const onIndustryChanged = useCallback((industry) => {
        setProductSelectionState(prevStat => ({
            ...prevStat,
            selectedCategories: [],
        }))
        void fetchIndustry({industryId: industry?.id})
    }, [fetchIndustry, setProductSelectionState])

    /**
     * Adds all the categories to the selected categories.
     * @type {(function(): void)}
     */
    const addAllCategories = useCallback(() => {
        const selectedIds = productSelectionState.selectedCategories.map(e => e.id);
        const remainingCategories = allCategories.filter(e => !selectedIds.includes(e.id));
        if (!remainingCategories.length)
            return;
        setProductSelectionState(prevState => ({
            ...prevState,
            selectedCategories: [...prevState.selectedCategories, ...remainingCategories],
        }))
        resetNextStates();
    }, [allCategories, productSelectionState.selectedCategories, setProductSelectionState, resetNextStates]);

    /**
     * Adds a category to the selected categories.
     * @type {(function(*): void)}
     */
    const addCategory = useCallback((category) => {
        setProductSelectionState(prevStat => ({
            ...prevStat,
            selectedCategories: [...prevStat.selectedCategories, category],
        }))
        resetNextStates();
    }, [setProductSelectionState, resetNextStates]);

    /**
     * Removes a category from the selected categories.
     * @type {(function(*): void)}
     */
    const removeCategory = useCallback((category) => {
        setProductSelectionState(prevStat => ({
            ...prevStat,
            selectedCategories: prevStat.selectedCategories.filter(e => e.id !== category.id),
        }))
        resetNextStates();
    }, [setProductSelectionState, resetNextStates]);

    /**
     * Adds a product to the selected products.
     * @type {(function(*): void)}
     */
    const addProduct = useCallback((product) => {
        setProductSelectionState(prevStat => ({
            ...prevStat,
            selectedProducts: [
                ...prevStat.selectedProducts,
                {
                    id: product.id,
                    partNo: product.partNo,
                }
            ],
        }))
        resetNextStates();
    }, [setProductSelectionState, resetNextStates])


    /**
     * Removes a product from the selected products.
     * @type {(function(*): void)}
     */
    const removeProduct = useCallback((product) => {
        setProductSelectionState(prevStat => ({
            ...prevStat,
            selectedProducts: prevStat.selectedProducts.filter(e => e.id !== product.id),
        }));
        resetNextStates();
    }, [setProductSelectionState, resetNextStates]);

    /**
     * Adds a tag to the selected tags.
     * @type {(function(*): void)}
     */
    const addTag = useCallback((tag) => {
        setProductSelectionState(prevStat => ({
            ...prevStat,
            selectedTags: [...prevStat.selectedTags, tag],
        }))
        resetNextStates();
    }, [setProductSelectionState, resetNextStates]);

    /**
     * Removes a tag from the selected tags.
     * @type {(function(*): void)}
     */
    const removeTag = useCallback((tag) => {
        setProductSelectionState(prevStat => ({
            ...prevStat,
            selectedTags: prevStat.selectedTags.filter(e => e.id !== tag.id),
        }))
        resetNextStates();
    }, [setProductSelectionState, resetNextStates]);


    /**
     * Adds a display tag to the selected tags.
     * @type {(function(*): void)}
     */
    const addDisplayTag = useCallback((tag) => {
        setProductSelectionState(prevStat => ({
            ...prevStat,
            displayTags: [...prevStat.displayTags, tag],
        }))
        resetNextStates();
    }, [setProductSelectionState, resetNextStates]);

    /**
     * Removes a display tag from the selected tags.
     * @type {(function(*): void)}
     */
    const removeDisplayTag = useCallback((tag) => {
        setProductSelectionState(prevStat => ({
            ...prevStat,
            displayTags: prevStat.displayTags.filter(e => e.id !== tag.id),
        }))
        resetNextStates();
    }, [setProductSelectionState, resetNextStates]);

    /**
     * Parses the csv file and adds the products to the list of products based on the serialized flag.
     * @type {(function(*, *): Promise<undefined|number|string>)|*}
     */
    const onCsvParsed = useCallback(async (parsed) => {
        let entries = parsed.map(({partNo}) => ({
            partNo: partNo?.trim(),
        }))
        if (!entries)
            return;

        const getUniqueEntries = (entries, fetcher) => [...new Set(entries.map((e) => fetcher(e)))];

        setImportingProductsFromCsv(true);
        const validationResponse = await Api.validatePartNumbers({
            partNumbers: getUniqueEntries(entries, e => e.partNo),
        })
        if (!isMounted())
            return;

        setImportingProductsFromCsv(false);

        if (!validationResponse?.resultFlag)
            return ToastService.error({
                title: "Error",
                message: "Error validating part numbers",
            })

        let validEntries = entries.filter((e) => !!validationResponse.data[e.partNo]);
        validEntries = getUniqueEntries(validEntries, e => e.partNo).map((e) => validEntries.find(e2 => e2.partNo === e));
        if (!validEntries.length)
            return ToastService.warning({
                title: "Warning",
                message: "No valid part numbers found",
            })

        setImportingProductsFromCsv(true);

        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: getUniqueEntries(validEntries, e => e.partNo),
        });

        if (!isMounted())
            return;

        setImportingProductsFromCsv(false);

        if (!partInfoResponse?.resultFlag)
            return ToastService.error({
                title: "Error",
                message: "Error getting part info",
            })

        const partInfoResponseData = Object.fromEntries(partInfoResponse.data.map(e => [e.partNo, e,]));

        validEntries = validEntries.map((e) => ({
            ...e,
            id: partInfoResponseData[e.partNo].id,
        }));

        setProductSelectionState(prevState => ({
            ...prevState,
            // unique by id
            selectedProducts: [
                ...prevState.selectedProducts,
                ...validEntries.filter(e => !prevState.selectedProducts.map(e => e.id).includes(e.id)),
            ],
        }));
        resetNextStates();
    }, [isMounted, setProductSelectionState, resetNextStates]);

    /**
     * Imports products from a csv file.
     * @type {(function(): Promise<undefined|number|string>)}
     */
    const importProductsFromCsv = useCallback(async () => {
        setUploadCsvFileDialogState({
            open: true,
            title: `Import products from Csv`,
            sampleFileTitle: `import-catalogue-products-from-csv`,
            requiredFields: [
                {
                    name: "partNo",
                    title: "Part No",
                    generator: (i) => `partNo ${i}`,
                },
            ],
            onParse: (parsed) => onCsvParsed(parsed),
        });
    }, [onCsvParsed]);

    return (
        <>
            <Row className={'w-100 mb-5 pb-5'}>
                <Col xs={12}>
                    <p className={'mx-2 text-sm text-color-1 mb-3'}>
                        Select the categories or tags that are associated with the products you would like to include in the Catalogue.
                        The Display Tags are the tags that you would like to include for each product.
                    </p>
                </Col>
                {
                    !hasNoIndustries &&
                    <Col xs={12} md={6} lg={4} className={'mb-5'}>
                        <Paper className={'paper d-flex flex-column h-100 p-3'}>
                            <div className={'w-100 mb-3  d-flex align-items-center justify-content-between'}>
                                <p className={'text-md font-weight-semi-bold'}>
                                    Categories
                                </p>
                                {
                                    allCategories.length !== productSelectionState.selectedCategories.length &&
                                    <Button
                                        variant={'text'}
                                        color={'primary'}
                                        onClick={addAllCategories}
                                        disabled={updatingStep}
                                    >
                                        {
                                            updatingStep
                                                ? "Loading..."
                                                : "Add All Categories"
                                        }
                                    </Button>
                                }
                            </div>
                            {
                                industries?.length > 1 &&
                                <div className={'d-flex w-100 mt-2 mb-2'}>
                                    <OutlinedDropdown
                                        label={'Industry'}
                                        fullWidth
                                        onChange={onIndustryChanged}
                                        value={fetchedIndustry}
                                        data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                                        disabled={updatingStep}
                                    />
                                </div>
                            }
                            <div className={'d-flex w-100 mt-2 mb-4'}>
                                <OutlinedDropdown
                                    label={'New Category'}
                                    onChange={addCategory}
                                    value={''}
                                    data={availableCategoryOptions}
                                    fullWidth
                                    disabled={updatingStep}
                                />
                            </div>
                            <div className={'d-flex flex-wrap w-100'}>
                                {
                                    productSelectionState.selectedCategories.map(category => (
                                        <Chips
                                            key={category.id}
                                            className={'mr-2 mb-2'}
                                            type={ChipsTypes.remove}
                                            content={getCascadingTitle(category)}
                                            onIconClick={() => removeCategory(category)}
                                        />
                                    ))
                                }
                            </div>
                        </Paper>
                    </Col>
                }
                <Col xs={12} md={6} lg={4} className={'mb-5'}>
                    <Paper className={'paper d-flex flex-column h-100 p-3'}>
                        <div className={'w-100 mb-3 d-flex align-items-center justify-content-between'}>
                            <p className={'text-md font-weight-semi-bold'}>
                                Products
                            </p>
                            <Button
                                variant={'text'}
                                color={'primary'}
                                onClick={importProductsFromCsv}
                                disabled={updatingStep || importingProductsFromCsv}
                            >
                                {
                                    updatingStep || importingProductsFromCsv
                                        ? "Loading..."
                                        : "Import Form CSV"
                                }
                            </Button>
                        </div>
                        <div className={'d-flex w-100 mt-2 mb-4'}>
                            <PartAutocomplete
                                clearOnBlur
                                fullWidth
                                label={"Add Product"}
                                value={null}
                                onChange={(_, value) => value && addProduct(value)}
                                filterOptions={(options) => options.filter(e => !existingPartIds.includes(e.id))}
                            />
                        </div>
                        <div className={'d-flex flex-wrap w-100'}>
                            {
                                productSelectionState.selectedProducts.map(product => (
                                    <Chips
                                        key={product.id}
                                        className={'mr-2 mb-2'}
                                        type={ChipsTypes.remove}
                                        contentType={ChipsContentType.element}
                                        content={
                                            <ProductInformationPopover partNo={product?.partNo ?? product?.partNumber}>
                                                {product?.partNo ?? product?.partNumber ?? ''}
                                            </ProductInformationPopover>
                                        }
                                        onIconClick={() => removeProduct(product)}
                                    />
                                ))
                            }
                        </div>
                    </Paper>
                </Col>
                <Col xs={12} md={6} lg={4} className={'mb-5'}>
                    <Paper className={'paper d-flex flex-column h-100 p-3'}>
                        <div className={'w-100 mb-3'}>
                            <p className={'text-md font-weight-semi-bold'}>
                                Tags
                            </p>

                        </div>
                        <div className={'d-flex w-100 mt-2 mb-4'}>
                            <TagAutocomplete
                                value={null}
                                clearOnBlur
                                fullWidth
                                selectedTags={productSelectionState.selectedTags ?? []}
                                disabled={updatingStep}
                                onChange={(e, t) => addTag(t)}
                                label={'New Tag'}
                            />
                        </div>
                        <div className={'d-flex flex-wrap w-100'}>
                            {
                                productSelectionState.selectedTags.map(tag => (
                                    <Chips
                                        key={tag.id}
                                        className={'mr-2 mb-2'}
                                        contentType={ChipsContentType.element}
                                        type={ChipsTypes.remove}
                                        iconPosition={ChipsIconPosition.start}
                                        onIconClick={() => removeTag(tag)}
                                        content={<TagChipsContent tag={tag} useIcon/>}
                                    />
                                ))
                            }
                        </div>
                    </Paper>
                </Col>
                <Col xs={12} className={'mb-5'}>
                    <Paper className={'paper d-flex flex-column h-100 p-3'}>
                        <div className={'w-100 mb-4'}>
                            <p className={'text-md font-weight-semi-bold'}>
                                Display Tags
                            </p>

                        </div>
                        <div className={'d-flex w-100 mt-2 mb-4'}>
                            <TagAutocomplete
                                value={null}
                                clearOnBlur
                                fullWidth
                                selectedTags={productSelectionState.displayTags ?? []}
                                disabled={updatingStep}
                                onChange={(e, t) => addDisplayTag(t)}
                                label={'New Display Tag'}
                            />
                        </div>
                        <div className={'d-flex flex-wrap w-100'}>
                            {
                                productSelectionState.displayTags.map(tag => (
                                    <Chips
                                        key={tag.id}
                                        className={'mr-2 mb-2'}
                                        contentType={ChipsContentType.element}
                                        type={ChipsTypes.remove}
                                        iconPosition={ChipsIconPosition.start}
                                        onIconClick={() => removeDisplayTag(tag)}
                                        content={<TagChipsContent tag={tag} useIcon/>}
                                    />
                                ))
                            }
                        </div>
                    </Paper>
                </Col>
            </Row>
            <UploadCsvFileDialog
                open={uploadCsvFileDialogState.open}
                close={() => setUploadCsvFileDialogState(initialUploadCsvFileDialogState)}
                title={uploadCsvFileDialogState.title}
                sampleFileTitle={uploadCsvFileDialogState.sampleFileTitle}
                requiredFields={uploadCsvFileDialogState.requiredFields}
                onParse={uploadCsvFileDialogState.onParse}
            />
        </>
    );
}


export default PrintCatalogueViewProductSelectionSection;
